/**
 * Story 25.1 — the theme store. One of the TWO sites allowed to set the
 * `dark` class on `<html>` (the other is the pre-paint bootstrap in
 * `index.html`; DW-32 guard 3 pins both, and nothing else).
 *
 * A module store read through `useSyncExternalStore` — never a React Context.
 * The preference (`system` default / `light` / `dark`) lives under one
 * browser-storage key, `THEME_STORAGE_KEY`, which the bootstrap repeats as a
 * literal (pinned by `__tests__/bootstrap.test.ts`). Every storage access is
 * wrapped: a blocked or throwing store reads as `system` and a failed write
 * still updates the in-memory preference.
 *
 * Story 25.8 turned dark mode on: every build follows the stored preference
 * (`system` by default, which tracks the OS live), and the Theme control in
 * the AvatarMenu / More page calls `setThemePreference`. Keep `resolveTheme`
 * in step with the bootstrap script in `index.html`.
 *
 * `theme-color`: `index.html` carries two scheme-keyed metas. On an explicit
 * Light / Dark choice both are set to the resolved `--canvas` read back from
 * `<html>` after the class flips (no hex literal here: the colour ratchet is
 * closed). On `system` each meta's original `content`, captured from the DOM
 * at module load, comes back. An empty read (jsdom) leaves the originals.
 */
import { useSyncExternalStore } from 'react';

export const THEME_STORAGE_KEY = 'amatis-theme';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeSnapshot {
  readonly preference: ThemePreference;
  readonly resolved: ResolvedTheme;
}

const DARK_QUERY = '(prefers-color-scheme: dark)';

function isPreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

/** Pure resolution, shared shape with the bootstrap in `index.html`. */
export function resolveTheme(
  preference: ThemePreference,
  osPrefersDark: boolean,
): ResolvedTheme {
  if (preference === 'dark') return 'dark';
  if (preference === 'light') return 'light';
  return osPrefersDark ? 'dark' : 'light';
}

export function readStoredPreference(): ThemePreference {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(value) ? value : 'system';
  } catch {
    return 'system';
  }
}

function writeStoredPreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Blocked storage: the preference still holds for this session.
  }
}

function darkQuery(): MediaQueryList | null {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(DARK_QUERY)
      : null;
  } catch {
    return null;
  }
}

function osPrefersDark(): boolean {
  return darkQuery()?.matches ?? false;
}

/** Each `theme-color` meta and its original `content`, captured at load. */
const themeColorMetas: ReadonlyArray<readonly [HTMLMetaElement, string | null]> =
  typeof document === 'undefined'
    ? []
    : Array.from(document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')).map(
        (meta) => [meta, meta.getAttribute('content')] as const,
      );

/**
 * Keeps the browser chrome colour in step with the theme. Explicit choice →
 * both metas take the resolved `--canvas`; `system` → the originals.
 */
function syncThemeColor(pref: ThemePreference): void {
  if (themeColorMetas.length === 0) return;
  let canvas = '';
  if (pref !== 'system') {
    try {
      canvas = getComputedStyle(document.documentElement).getPropertyValue('--canvas').trim();
    } catch {
      canvas = '';
    }
  }
  for (const [meta, original] of themeColorMetas) {
    const next = canvas || original;
    if (next === null) meta.removeAttribute('content');
    else meta.setAttribute('content', next);
  }
}

/**
 * Puts the resolved theme on `<html>`: the `dark` class and `color-scheme`,
 * then syncs the `theme-color` metas.
 * Module-private on purpose: `setThemePreference` is the only public route to
 * the class, so no third site can set it while the store says otherwise.
 */
function applyTheme(resolved: ResolvedTheme, pref: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
  syncThemeColor(pref);
}

let preference: ThemePreference = readStoredPreference();
let snapshot: ThemeSnapshot = {
  preference,
  resolved: resolveTheme(preference, osPrefersDark()),
};
const listeners = new Set<() => void>();

/** Recomputes the snapshot; replaces it (and notifies) only on a change. */
function refresh(): void {
  const resolved = resolveTheme(preference, osPrefersDark());
  applyTheme(resolved, preference);
  if (snapshot.preference === preference && snapshot.resolved === resolved) return;
  snapshot = { preference, resolved };
  for (const listener of listeners) listener();
}

export function setThemePreference(next: ThemePreference): void {
  preference = isPreference(next) ? next : 'system';
  writeStoredPreference(preference);
  refresh();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSnapshot(): ThemeSnapshot {
  return snapshot;
}

export function useTheme(): ThemeSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// Boot: apply once, then let `system` follow the OS live.
applyTheme(snapshot.resolved, preference);
const query = darkQuery();
if (query) {
  const onChange = () => refresh();
  if (typeof query.addEventListener === 'function') query.addEventListener('change', onChange);
  else if (typeof query.addListener === 'function') query.addListener(onChange);
}
